export type PreviewMode = "modern" | "narrow" | "images-disabled" | "outlook";

export type PreviewLayout = {
  padding: number;
  sourceWidth: number;
  sourceHeight: number;
  viewportWidth: number;
  viewportHeight: number;
  scale: number;
  renderedWidth: number;
  renderedHeight: number;
  narrowClientWidth: number | null;
};

type PreviewLayoutInput = {
  mode: PreviewMode;
  sourceWidth: number;
  sourceHeight: number;
  frameWidth: number;
};

const PREVIEW_PADDING = 24;
const NARROW_CLIENT_WIDTH = 320;

const positiveDimension = (value: number, fallback: number): number =>
  Number.isFinite(value) && value > 0 ? value : fallback;

export const calculatePreviewLayout = ({
  mode,
  sourceWidth,
  sourceHeight,
  frameWidth,
}: PreviewLayoutInput): PreviewLayout => {
  const safeSourceWidth = positiveDimension(sourceWidth, 1);
  const safeSourceHeight = positiveDimension(sourceHeight, 1);
  const narrowClientWidth = mode === "narrow" ? NARROW_CLIENT_WIDTH : null;
  const viewportWidth =
    (narrowClientWidth ?? safeSourceWidth) + PREVIEW_PADDING * 2;
  const viewportHeight = safeSourceHeight + PREVIEW_PADDING * 2;
  const safeFrameWidth = positiveDimension(frameWidth, viewportWidth);
  const scale = Math.min(1, safeFrameWidth / viewportWidth);

  return {
    padding: PREVIEW_PADDING,
    sourceWidth: safeSourceWidth,
    sourceHeight: safeSourceHeight,
    viewportWidth,
    viewportHeight,
    scale,
    renderedWidth: viewportWidth * scale,
    renderedHeight: viewportHeight * scale,
    narrowClientWidth,
  };
};

const withoutBackgroundImages = (fragment: string): string =>
  fragment
    .replace(/\sbackground="[^"]*"/gi, "")
    .replace(/background-image:url\([^)]*\);?/gi, "")
    .replace(/<v:fill\b[^>]*\/>/gi, "");

const cssNumber = (value: number): string =>
  Number(value.toFixed(6)).toString();

export const createPreviewDocument = (
  fragment: string,
  mode: PreviewMode,
  source: Pick<PreviewLayout, "sourceWidth" | "sourceHeight">
): string => {
  const sourceWidth = positiveDimension(source.sourceWidth, 1);
  const sourceHeight = positiveDimension(source.sourceHeight, 1);
  const viewportWidth =
    (mode === "narrow" ? NARROW_CLIENT_WIDTH : sourceWidth) +
    PREVIEW_PADDING * 2;
  const viewportHeight = sourceHeight + PREVIEW_PADDING * 2;
  const visibleFragment =
    mode === "images-disabled" ? withoutBackgroundImages(fragment) : fragment;
  const outlookStyle =
    mode === "outlook"
      ? "table{mso-table-lspace:0;mso-table-rspace:0}td{background-size:auto 100%!important}"
      : "";
  const narrowClientStyle =
    mode === "narrow"
      ? `.email-thumbnail-preview-client{width:${NARROW_CLIENT_WIDTH}px;height:${cssNumber(
          sourceHeight
        )}px;overflow:hidden}`
      : "";
  const clientOpen =
    mode === "narrow" ? '<div class="email-thumbnail-preview-client">' : "";
  const clientClose = mode === "narrow" ? "</div>" : "";

  return [
    "<!doctype html>",
    '<html><head><meta charset="utf-8" />',
    '<meta name="viewport" content="width=device-width, initial-scale=1" />',
    `<style>html,body{margin:0;width:${cssNumber(
      viewportWidth
    )}px;height:${cssNumber(
      viewportHeight
    )}px;background:#f4f6fa}body{box-sizing:border-box;overflow:hidden;padding:${PREVIEW_PADDING}px;font-family:Arial,Helvetica,sans-serif}.email-thumbnail-preview-canvas{width:${cssNumber(
      sourceWidth
    )}px;height:${cssNumber(
      sourceHeight
    )}px}${narrowClientStyle}${outlookStyle}</style>`,
    `</head><body>${clientOpen}<div class="email-thumbnail-preview-canvas">`,
    visibleFragment ||
      '<div style="padding:32px;color:#64748b;background:#fff;border:1px solid #d9e0ea">Save a valid configuration to generate the preview.</div>',
    `</div>${clientClose}</body></html>`,
  ].join("");
};
